import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminLogin = () => {
  const { user, loading, setupRequired, login, setupOwner } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500">Loading…</div>;
  if (user) {
    const from = location.state?.from?.pathname || '/admin';
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (setupRequired) {
        if (password.length < 8) { toast.error('Password must be at least 8 characters'); setSubmitting(false); return; }
        await setupOwner(email, name, password);
        toast.success('Owner account created. Welcome!');
      } else {
        await login(email, password);
        toast.success('Logged in');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6" style={{ backgroundImage: 'radial-gradient(ellipse at center, rgba(212,175,55,0.12) 0%, rgba(0,0,0,0) 60%)' }}>
      <Card className="bg-zinc-950 border-zinc-900 p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <img data-testid="admin-login-brand-logo" src="https://customer-assets.emergentagent.com/job_vape-shop-crypto/artifacts/9lx9auiu_logo.webp" alt="We Supply Smoke" className="h-20 w-auto mx-auto mb-3 object-contain drop-shadow-[0_0_20px_rgba(212,175,55,0.4)]" />
          <h1 className="serif-italic gold-gradient-text text-3xl">{setupRequired ? 'Create Owner Account' : 'Admin Sign In'}</h1>
          <p className="text-zinc-500 text-sm mt-2">{setupRequired ? 'First-time setup — create your owner account.' : 'Sign in to manage your store.'}</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          {setupRequired && (
            <div><Label className="text-zinc-300">Your name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} className="bg-transparent border-zinc-800 mt-1" placeholder="e.g. Jonathan Brand" /></div>
          )}
          <div><Label className="text-zinc-300">Email</Label><Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-transparent border-zinc-800 mt-1" autoFocus /></div>
          <div><Label className="text-zinc-300">Password</Label><Input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-transparent border-zinc-800 mt-1" placeholder={setupRequired ? 'Min. 8 characters' : ''} /></div>
          <Button type="submit" disabled={submitting} className="btn-gold w-full h-11 tracking-wider uppercase">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {setupRequired ? 'Creating…' : 'Signing in…'}</> : (setupRequired ? 'Create owner' : 'Sign in')}
          </Button>
        </form>
        {!setupRequired && (
          <p className="text-zinc-500 text-xs text-center mt-6">Forgot password? Ask another owner-level user to reset it from User Management.</p>
        )}
      </Card>
    </div>
  );
};

export default AdminLogin;
